import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import OperisLoader from '../../components/common/OperisLoader';
import { ReportsGeneratingSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';
import {
  Brain, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Clock, Users, Zap, Target, RefreshCw, Shield, Activity,
  ChevronDown, ChevronUp, Info, BarChart3, ArrowRight,
  Save, History, Trash2, Eye, X, FileText
} from 'lucide-react';
import { getKpiAiAnalysis, saveKpiAiAnalysis, getSavedAnalyses, getSavedAnalysisById, deleteSavedAnalysis } from '../../api/kpi.api';
import Chart from 'react-apexcharts';

/* ═══════════════════════════════════════════════════════════════ */
/*  AI-Powered KPI Analytics Dashboard                            */
/*  Features: Performance prediction, Bottleneck detection,       */
/*  NL insights, Trend forecasting, Team comparison, MAE/RAE      */
/* ═══════════════════════════════════════════════════════════════ */

const SEVERITY_COLORS = {
  Critical: { bg: 'bg-red-50 dark:bg-red-500/10', text: 'text-red-600 dark:text-red-400', border: 'border-red-200 dark:border-red-800/40' },
  High:     { bg: 'bg-orange-50 dark:bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800/40' },
  Medium:   { bg: 'bg-yellow-50 dark:bg-yellow-500/10', text: 'text-yellow-600 dark:text-yellow-400', border: 'border-yellow-200 dark:border-yellow-800/40' },
  Low:      { bg: 'bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800/40' },
};

const SENTIMENT_ICONS = {
  positive: { Icon: CheckCircle, color: 'text-green-500' },
  neutral:  { Icon: Info, color: 'text-blue-500' },
  negative: { Icon: AlertTriangle, color: 'text-red-500' },
};

const ICON_MAP = {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, Users, Zap, Target, Info, Activity, BarChart3, Shield,
};

const KpiAiDashboardPage = () => {
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [expandedSections, setExpandedSections] = useState({
    prediction: true, bottlenecks: true, insights: true, forecast: true, comparison: true, metrics: false
  });

  /* ─── History & Save state ─── */
  const [showHistory, setShowHistory] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [viewingSavedId, setViewingSavedId] = useState(null); // non-null when viewing a past analysis
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const toggle = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = {};
      if (dateRange.startDate) body.startDate = dateRange.startDate;
      if (dateRange.endDate) body.endDate = dateRange.endDate;
      const res = await getKpiAiAnalysis(body);
      setAnalysis(res.data?.data);
    } catch (err) {
      console.error('AI analysis error:', err);
      setError(err.response?.data?.message || 'Failed to generate AI analysis. Please try again.');
    }
    setLoading(false);
  }, [dateRange]);

  /* ─── Load history list ─── */
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await getSavedAnalyses();
      setSavedList(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
    setHistoryLoading(false);
  }, []);

  /* ─── Open history panel ─── */
  const openHistory = useCallback(() => {
    setShowHistory(true);
    loadHistory();
  }, [loadHistory]);

  /* ─── Save current analysis ─── */
  const handleSave = useCallback(async () => {
    if (!analysis) return;
    setSaving(true);
    try {
      const payload = {
        title: saveTitle.trim() || undefined,
        notes: saveNotes.trim() || undefined,
        analysis,
        filterStartDate: dateRange.startDate || undefined,
        filterEndDate: dateRange.endDate || undefined,
      };
      await saveKpiAiAnalysis(payload);
      setShowSaveModal(false);
      setSaveTitle('');
      setSaveNotes('');
      // refresh history if panel is open
      if (showHistory) loadHistory();
    } catch (err) {
      console.error('Save failed:', err);
    }
    setSaving(false);
  }, [analysis, saveTitle, saveNotes, dateRange, showHistory, loadHistory]);

  /* ─── View a saved analysis ─── */
  const viewSaved = useCallback(async (id) => {
    setHistoryLoading(true);
    try {
      const res = await getSavedAnalysisById(id);
      const data = res.data?.data;
      if (data?.analysis) {
        setAnalysis(data.analysis);
        setViewingSavedId(id);
        setShowHistory(false);
      }
    } catch (err) {
      console.error('Failed to load saved analysis:', err);
    }
    setHistoryLoading(false);
  }, []);

  /* ─── Delete a saved analysis ─── */
  const handleDelete = useCallback(async (id) => {
    try {
      await deleteSavedAnalysis(id);
      setSavedList(prev => prev.filter(s => s.id !== id));
      if (viewingSavedId === id) {
        setViewingSavedId(null);
        setAnalysis(null);
      }
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  }, [viewingSavedId]);

  /* ─── Back to live mode ─── */
  const exitSavedView = useCallback(() => {
    setViewingSavedId(null);
    setAnalysis(null);
  }, []);

  /* auto-load history on mount */
  useEffect(() => { loadHistory(); }, [loadHistory]);

  /* ─── Chart theme (respects dark mode) ─── */
  const isDark = document.documentElement.classList.contains('dark');
  const chartTheme = {
    mode: isDark ? 'dark' : 'light',
    palette: 'palette1',
    monochrome: { enabled: false },
  };
  const accentColor = getComputedStyle(document.documentElement).getPropertyValue('--accent-500').trim() || '#f59e0b';
  const accent400 = getComputedStyle(document.documentElement).getPropertyValue('--accent-400').trim() || '#fbbf24';
  const accent600 = getComputedStyle(document.documentElement).getPropertyValue('--accent-600').trim() || '#d97706';

  /* ─── Section Header ─── */
  const SectionHeader = ({ icon: Icon, title, expanded, onToggle, badge }) => (
    <button onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors rounded-t-2xl">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-500/10">
          <Icon className="w-5 h-5 text-indigo-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        {badge && <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 font-medium">{badge}</span>}
      </div>
      {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
    </button>
  );

  /* ─── Health Score Circle ─── */
  const HealthScoreCircle = ({ score, label }) => {
    const num = parseInt(score) || 0;
    const color = num >= 80 ? '#22c55e' : num >= 60 ? '#f59e0b' : num >= 40 ? '#f97316' : '#ef4444';
    return (
      <div className="flex flex-col items-center">
        <div className="relative w-32 h-32">
          <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="52" stroke="currentColor" strokeWidth="8"
              className="text-gray-200 dark:text-gray-700" fill="none" />
            <circle cx="60" cy="60" r="52" stroke={color} strokeWidth="8" fill="none"
              strokeDasharray={`${(num / 100) * 327} 327`}
              strokeLinecap="round" className="transition-all duration-1000" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">{score}</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">/ 100</span>
          </div>
        </div>
        <span className="mt-2 text-sm font-semibold" style={{ color }}>{label}</span>
      </div>
    );
  };

  /* ─── Performance Prediction Cards ─── */
  const renderPrediction = () => {
    const pp = analysis.performancePrediction;
    if (!pp) return null;

    const metrics = [
      { label: 'Avg Hours / Day', current: pp.currentAvgHoursPerDay, predicted: pp.predictedAvgHoursNextWeek, icon: Clock },
      { label: 'Completion Rate', current: pp.currentCompletionRate, predicted: pp.predictedCompletionRateNextWeek, icon: Target, suffix: '%' },
      { label: 'Task Velocity / Week', current: pp.currentTaskVelocity, predicted: pp.predictedTaskVelocityNextWeek, icon: Zap },
    ];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {metrics.map((m, i) => {
            const diff = m.predicted - m.current;
            const isUp = diff > 0;
            return (
              <div key={i} className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <m.icon className="w-4 h-4 text-indigo-500" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{m.label}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">{m.current}{m.suffix || ''}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                  <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{m.predicted}{m.suffix || ''}</span>
                </div>
                <div className={`mt-2 flex items-center gap-1 text-xs font-medium ${isUp ? 'text-green-500' : diff < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                  {isUp ? <TrendingUp className="w-3 h-3" /> : diff < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                  {diff > 0 ? '+' : ''}{diff.toFixed(1)}{m.suffix || ''} predicted change
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-start gap-3 bg-indigo-50/60 dark:bg-indigo-500/5 rounded-xl p-4 border border-indigo-100 dark:border-indigo-800/30">
          <Brain className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">AI Analysis</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                pp.confidence === 'High' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' :
                pp.confidence === 'Medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400' :
                'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
              }`}>
                {pp.confidence} Confidence
              </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">{pp.analysis}</p>
          </div>
        </div>
      </div>
    );
  };

  /* ─── Bottleneck List ─── */
  const renderBottlenecks = () => (
    <div className="space-y-3">
      {analysis.bottlenecks.map((b, i) => {
        const sev = SEVERITY_COLORS[b.severity] || SEVERITY_COLORS.Medium;
        return (
          <div key={i} className={`rounded-xl border p-4 ${sev.bg} ${sev.border}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-4 h-4 ${sev.text}`} />
                <span className="font-semibold text-gray-900 dark:text-white">{b.area}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold ${sev.text} uppercase`}>{b.severity}</span>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">Impact</span>
                  <div className="w-16 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{
                      width: `${(b.impactScore / 10) * 100}%`,
                      backgroundColor: b.impactScore >= 7 ? '#ef4444' : b.impactScore >= 4 ? '#f59e0b' : '#3b82f6'
                    }} />
                  </div>
                  <span className="text-xs font-bold text-gray-600 dark:text-gray-300">{b.impactScore}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{b.description}</p>
            <div className="flex items-start gap-2 bg-white/60 dark:bg-gray-800/40 rounded-lg p-2.5">
              <Zap className="w-3.5 h-3.5 text-indigo-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-600 dark:text-gray-400"><span className="font-semibold text-indigo-600 dark:text-indigo-400">Recommendation:</span> {b.recommendation}</p>
            </div>
          </div>
        );
      })}
    </div>
  );

  /* ─── NL Insights ─── */
  const renderInsights = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {analysis.insights.map((ins, i) => {
        const sentimentInfo = SENTIMENT_ICONS[ins.sentiment] || SENTIMENT_ICONS.neutral;
        const IconComp = ICON_MAP[ins.icon] || Info;
        return (
          <div key={i} className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50 hover:shadow-sm transition-shadow">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10">
                <IconComp className="w-4 h-4 text-indigo-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{ins.title}</h4>
                  <sentimentInfo.Icon className={`w-3.5 h-3.5 flex-shrink-0 ${sentimentInfo.color}`} />
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{ins.description}</p>
                <span className="inline-block mt-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">{ins.category}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  /* ─── Trend Forecast Charts ─── */
  const renderForecast = () => {
    const tf = analysis.trendForecast;
    if (!tf) return null;

    const buildChartData = (data, label) => {
      const categories = data.map(d => d.label);
      const actuals = data.map(d => d.isForecast ? null : d.actual);
      const predicted = data.map(d => d.isForecast ? d.predicted : null);

      return {
        series: [
          { name: `${label} (Actual)`, data: actuals, type: 'area' },
          { name: `${label} (Forecast)`, data: predicted, type: 'area' },
        ],
        options: {
          chart: { height: 260, toolbar: { show: false }, background: 'transparent', fontFamily: 'Inter, sans-serif' },
          theme: chartTheme,
          colors: [accentColor, accent400],
          dataLabels: { enabled: false },
          stroke: { curve: 'smooth', width: [3, 3], dashArray: [0, 5] },
          fill: {
            type: 'gradient',
            gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] },
          },
          xaxis: { categories, labels: { style: { colors: isDark ? '#9ca3af' : '#6b7280', fontSize: '11px' } } },
          yaxis: { labels: { style: { colors: isDark ? '#9ca3af' : '#6b7280', fontSize: '11px' } } },
          grid: { borderColor: isDark ? '#374151' : '#f1f5f9', strokeDashArray: 3 },
          tooltip: { shared: true, intersect: false },
          legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px', labels: { colors: isDark ? '#d1d5db' : '#374151' } },
          annotations: {
            xaxis: [{
              x: data.findIndex(d => d.isForecast) > 0 ? categories[data.findIndex(d => d.isForecast)] : undefined,
              borderColor: accent600,
              strokeDashArray: 4,
              label: { text: 'Forecast →', style: { color: '#fff', background: accent600, fontSize: '10px', padding: { left: 6, right: 6, top: 2, bottom: 2 } } }
            }]
          },
        },
      };
    };

    const TrendBadge = ({ dir }) => (
      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
        dir === 'Improving' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' :
        dir === 'Declining' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' :
        'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
      }`}>
        {dir === 'Improving' ? '↑' : dir === 'Declining' ? '↓' : '~'} {dir}
      </span>
    );

    const hoursChart = tf.hoursForecast?.length > 0 ? buildChartData(tf.hoursForecast, 'Hours') : null;
    const tasksChart = tf.taskCompletionForecast?.length > 0 ? buildChartData(tf.taskCompletionForecast, 'Tasks') : null;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {hoursChart && (
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Hours Logged Trend</span>
                <TrendBadge dir={tf.hoursTrendDirection} />
              </div>
              <Chart options={hoursChart.options} series={hoursChart.series} type="area" height={260} />
            </div>
          )}
          {tasksChart && (
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Task Completion Trend</span>
                <TrendBadge dir={tf.tasksTrendDirection} />
              </div>
              <Chart options={tasksChart.options} series={tasksChart.series} type="area" height={260} />
            </div>
          )}
        </div>
        {tf.analysis && (
          <div className="bg-indigo-50/60 dark:bg-indigo-500/5 rounded-xl p-4 border border-indigo-100 dark:border-indigo-800/30">
            <div className="flex items-start gap-2">
              <Brain className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700 dark:text-gray-300">{tf.analysis}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ─── Team Comparison (Radar + Bar) ─── */
  const renderTeamComparison = () => {
    const teams = analysis.teamComparison;
    if (!teams?.length) return null;

    const radarOptions = {
      chart: { height: 350, toolbar: { show: false }, background: 'transparent', fontFamily: 'Inter, sans-serif' },
      theme: chartTheme,
      colors: [accentColor, '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#f97316', '#14b8a6'],
      xaxis: {
        categories: ['Efficiency', 'Quality', 'Velocity', 'Reliability'],
        labels: { style: { colors: isDark ? '#9ca3af' : '#6b7280', fontSize: '12px' } },
      },
      yaxis: { show: false, max: 100 },
      plotOptions: { radar: { polygons: { strokeColors: isDark ? '#374151' : '#e5e7eb', connectorColors: isDark ? '#374151' : '#e5e7eb' } } },
      legend: { position: 'bottom', fontSize: '11px', labels: { colors: isDark ? '#d1d5db' : '#374151' } },
      stroke: { width: 2 },
      fill: { opacity: 0.15 },
      markers: { size: 3 },
    };

    const radarSeries = teams.map(t => ({
      name: t.teamName,
      data: [t.efficiency, t.quality, t.velocity, t.reliability],
    }));

    const barOptions = {
      chart: { height: 300, toolbar: { show: false }, background: 'transparent', fontFamily: 'Inter, sans-serif' },
      theme: chartTheme,
      colors: teams.map((_, i) => {
        const palette = [accentColor, '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6'];
        return palette[i % palette.length];
      }),
      plotOptions: {
        bar: { horizontal: true, borderRadius: 6, barHeight: '60%', distributed: true },
      },
      dataLabels: { enabled: true, style: { fontSize: '12px', fontWeight: 600 }, formatter: (v) => `${v}` },
      xaxis: { max: 100, labels: { style: { colors: isDark ? '#9ca3af' : '#6b7280' } } },
      yaxis: { labels: { style: { colors: isDark ? '#9ca3af' : '#6b7280', fontSize: '12px' } } },
      legend: { show: false },
      grid: { borderColor: isDark ? '#374151' : '#f1f5f9' },
      tooltip: { enabled: false },
    };

    const barSeries = [{
      name: 'Overall Score',
      data: teams.map(t => ({ x: `${t.rank} ${t.teamName}`, y: t.overallScore })),
    }];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Team Radar — Multi-Dimension</h4>
            <Chart options={radarOptions} series={radarSeries} type="radar" height={350} />
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Overall Score Ranking</h4>
            <Chart options={barOptions} series={barSeries} type="bar" height={300} />
          </div>
        </div>
        {/* Team Detail Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {teams.map((t, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{t.rank}</span>
                  <span className="font-semibold text-gray-900 dark:text-white text-sm">{t.teamName}</span>
                </div>
                <span className="text-xl font-bold text-gray-900 dark:text-white">{t.overallScore}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {[
                  { label: 'Efficiency', val: t.efficiency },
                  { label: 'Quality', val: t.quality },
                  { label: 'Velocity', val: t.velocity },
                  { label: 'Reliability', val: t.reliability },
                ].map((m, j) => (
                  <div key={j}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400">{m.label}</span>
                      <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300">{m.val}</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${m.val}%`, backgroundColor: accentColor }} />
                    </div>
                  </div>
                ))}
              </div>
              {t.strengths && (
                <p className="text-[10px] text-green-600 dark:text-green-400 mb-1">
                  <span className="font-semibold">Strengths:</span> {t.strengths}
                </p>
              )}
              {t.weaknesses && (
                <p className="text-[10px] text-orange-600 dark:text-orange-400">
                  <span className="font-semibold">Improve:</span> {t.weaknesses}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ─── Error Metrics (MAE/RAE) ─── */
  const renderErrorMetrics = () => {
    const em = analysis.errorMetrics;
    if (!em) return null;

    const MetricCard = ({ label, value, description }) => (
      <div className="bg-gray-50 dark:bg-gray-700/40 rounded-xl p-4">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">{description}</p>
      </div>
    );

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="MAE (Hours)" value={em.maE_Hours ?? em.mae_Hours ?? 0} description="Mean Absolute Error — average prediction deviation in hours" />
          <MetricCard label="RAE (Hours)" value={em.raE_Hours ?? em.rae_Hours ?? 0} description="Relative Absolute Error — ratio vs. baseline predictor" />
          <MetricCard label="MAE (Tasks)" value={em.maE_Tasks ?? em.mae_Tasks ?? 0} description="Mean Absolute Error — average task prediction deviation" />
          <MetricCard label="RAE (Tasks)" value={em.raE_Tasks ?? em.rae_Tasks ?? 0} description="Relative Absolute Error — task prediction reliability" />
        </div>
        {em.interpretation && (
          <div className="bg-indigo-50/60 dark:bg-indigo-500/5 rounded-xl p-4 border border-indigo-100 dark:border-indigo-800/30">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700 dark:text-gray-300">{em.interpretation}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ═══════════════════ MAIN RENDER ═══════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50/30 dark:from-gray-900 dark:to-indigo-950/20 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        <PageFadeSection>
        {/* ─── Header ─── */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Analytics</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Powered by Azure AI — Performance prediction, bottleneck detection & trend forecasting</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <input type="date" value={dateRange.startDate} onChange={(e) => setDateRange(p => ({ ...p, startDate: e.target.value }))}
                  className="text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-2 focus:ring-2 focus:ring-indigo-500/30" />
                <span className="text-gray-400 text-xs">to</span>
                <input type="date" value={dateRange.endDate} onChange={(e) => setDateRange(p => ({ ...p, endDate: e.target.value }))}
                  className="text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-2 focus:ring-2 focus:ring-indigo-500/30" />
              </div>
              {/* History button */}
              <button onClick={openHistory}
                className="relative flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-all">
                <History className="w-4 h-4" />
                History
                {savedList.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                    {savedList.length}
                  </span>
                )}
              </button>
              {/* Save button (only when analysis exists) */}
              {analysis && !viewingSavedId && (
                <button onClick={() => setShowSaveModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-indigo-200 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-sm font-medium hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-all">
                  <Save className="w-4 h-4" />
                  Save
                </button>
              )}
              <button onClick={() => { setViewingSavedId(null); fetchAnalysis(); }} disabled={loading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white font-medium text-sm shadow-md shadow-indigo-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                {loading ? 'Analyzing…' : analysis ? 'Re-analyze' : 'Generate Analysis'}
              </button>
            </div>
          </div>
        </div>
        </PageFadeSection>

        {/* ─── Loading State ─── */}
        {loading && (
          <PageFadeSection>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
            <ReportsGeneratingSkeleton />
          </div>
          </PageFadeSection>
        )}

        {/* ─── Error State ─── */}
        {error && !loading && (
          <PageFadeSection>
          <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800/40 rounded-2xl p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <p className="text-sm text-red-700 dark:text-red-400 font-medium">{error}</p>
            <button onClick={fetchAnalysis} className="mt-3 px-4 py-2 text-xs rounded-lg bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 font-medium hover:bg-red-200 dark:hover:bg-red-500/30 transition">
              Retry
            </button>
          </div>
          </PageFadeSection>
        )}

        {/* ─── Empty State ─── */}
        {!analysis && !loading && !error && (
          <PageFadeSection>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
              <Brain className="w-10 h-10 text-indigo-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">AI-Powered Analytics</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
              Click "Generate Analysis" to run Azure AI on your KPI data. Get performance predictions, 
              bottleneck detection, trend forecasting, team comparisons, and natural language insights.
            </p>
            <button onClick={fetchAnalysis}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white font-medium text-sm shadow-lg shadow-indigo-500/25 transition-all">
              <Brain className="w-4 h-4 inline mr-2" />
              Generate Analysis
            </button>
          </div>
          </PageFadeSection>
        )}

        {/* ─── Analysis Results ─── */}
        {analysis && !loading && (
          <PageFadeSection>
          <div className="space-y-4">

            {/* Viewing Saved Banner */}
            {viewingSavedId && (
              <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-700/40 rounded-2xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Viewing Saved Analysis</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">This is a past analysis loaded from history. Click "Re-analyze" to generate a fresh one.</p>
                  </div>
                </div>
                <button onClick={exitSavedView}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-500/30 transition">
                  <X className="w-3.5 h-3.5" /> Close
                </button>
              </div>
            )}

            {/* Health Score + Quick Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <HealthScoreCircle score={analysis.overallHealthScore} label={analysis.healthLabel} />
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">{analysis.bottlenecks?.length || 0}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Bottlenecks Found</p>
                  </div>
                  <div className="text-center">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">{analysis.insights?.length || 0}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">AI Insights</p>
                  </div>
                  <div className="text-center">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">{analysis.teamComparison?.length || 0}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Teams Analyzed</p>
                  </div>
                  <div className="text-center">
                    <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{analysis.performancePrediction?.confidence || 'N/A'}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Prediction Confidence</p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-4 text-right">
                Generated {new Date(analysis.generatedAt).toLocaleString()}
              </p>
            </div>

            {/* Performance Prediction */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
              <SectionHeader icon={TrendingUp} title="Performance Prediction" expanded={expandedSections.prediction} onToggle={() => toggle('prediction')} badge="Next Week" />
              {expandedSections.prediction && <div className="px-4 pb-4">{renderPrediction()}</div>}
            </div>

            {/* Bottleneck Detection */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
              <SectionHeader icon={AlertTriangle} title="Bottleneck Detection" expanded={expandedSections.bottlenecks} onToggle={() => toggle('bottlenecks')}
                badge={`${analysis.bottlenecks?.filter(b => b.severity === 'Critical' || b.severity === 'High').length || 0} Critical/High`} />
              {expandedSections.bottlenecks && <div className="px-4 pb-4">{renderBottlenecks()}</div>}
            </div>

            {/* NL Insights */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
              <SectionHeader icon={Brain} title="AI Insights" expanded={expandedSections.insights} onToggle={() => toggle('insights')} badge={`${analysis.insights?.length || 0} insights`} />
              {expandedSections.insights && <div className="px-4 pb-4">{renderInsights()}</div>}
            </div>

            {/* Trend Forecasting */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
              <SectionHeader icon={Activity} title="Trend Forecasting" expanded={expandedSections.forecast} onToggle={() => toggle('forecast')} badge="4 Week + Prediction" />
              {expandedSections.forecast && <div className="px-4 pb-4">{renderForecast()}</div>}
            </div>

            {/* Team Comparison */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
              <SectionHeader icon={Users} title="Team Comparison" expanded={expandedSections.comparison} onToggle={() => toggle('comparison')} badge={`${analysis.teamComparison?.length || 0} teams`} />
              {expandedSections.comparison && <div className="px-4 pb-4">{renderTeamComparison()}</div>}
            </div>

            {/* MAE / RAE Error Metrics */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
              <SectionHeader icon={Shield} title="Prediction Accuracy (MAE / RAE)" expanded={expandedSections.metrics} onToggle={() => toggle('metrics')} badge="Error Metrics" />
              {expandedSections.metrics && <div className="px-4 pb-4">{renderErrorMetrics()}</div>}
            </div>

          </div>
          </PageFadeSection>
        )}

      {/* ═══════════ Save Modal ═══════════ */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowSaveModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/50 w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-500/10">
                <Save className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Save Analysis</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Save this analysis to review later with all charts intact</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Title (optional)</label>
                <input type="text" value={saveTitle} onChange={e => setSaveTitle(e.target.value)}
                  placeholder={`Analysis – ${new Date().toLocaleDateString()}`}
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-2.5 focus:ring-2 focus:ring-indigo-500/30 focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Notes (optional)</label>
                <textarea value={saveNotes} onChange={e => setSaveNotes(e.target.value)} rows={3}
                  placeholder="Add any notes about this analysis…"
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-2.5 focus:ring-2 focus:ring-indigo-500/30 focus:outline-none resize-none" />
              </div>
              {/* Quick preview */}
              <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl p-3">
                <div className="text-center">
                  <span className="text-xl font-bold text-gray-900 dark:text-white">{analysis?.overallHealthScore || 'N/A'}</span>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Health Score</p>
                </div>
                <div className="h-8 w-px bg-gray-200 dark:bg-gray-600" />
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <p>{analysis?.bottlenecks?.length || 0} bottlenecks · {analysis?.insights?.length || 0} insights</p>
                  <p>{analysis?.teamComparison?.length || 0} teams · {dateRange.startDate || 'All time'} – {dateRange.endDate || 'Now'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white text-sm font-medium shadow-md shadow-indigo-500/25 transition-all disabled:opacity-50">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving…' : 'Save Analysis'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ History Slide-out Panel ═══════════ */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setShowHistory(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          {/* Panel */}
          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl border-l border-gray-100 dark:border-gray-700/50 flex flex-col animate-slide-in-right"
            onClick={e => e.stopPropagation()}>
            {/* Panel Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-500/10">
                  <History className="w-5 h-5 text-indigo-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Analysis History</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{savedList.length} saved {savedList.length === 1 ? 'analysis' : 'analyses'}</p>
                </div>
              </div>
              <button onClick={() => setShowHistory(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {historyLoading && savedList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <OperisLoader size="md" />
                  <p className="mt-3 text-xs text-gray-400">Loading history…</p>
                </div>
              ) : savedList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                    <FileText className="w-8 h-8 text-gray-300 dark:text-gray-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No saved analyses yet</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Generate an analysis and click Save to store it here</p>
                </div>
              ) : (
                savedList.map(item => {
                  const scoreNum = parseInt(item.overallHealthScore) || 0;
                  const scoreColor = scoreNum >= 80 ? 'text-green-500' : scoreNum >= 60 ? 'text-amber-500' : scoreNum >= 40 ? 'text-orange-500' : 'text-red-500';
                  const isActive = viewingSavedId === item.id;
                  return (
                    <div key={item.id}
                      className={`rounded-xl border p-4 transition-all hover:shadow-md cursor-pointer group ${
                        isActive
                          ? 'bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-700/40'
                          : 'bg-white dark:bg-gray-700/40 border-gray-100 dark:border-gray-700/50 hover:border-indigo-200 dark:hover:border-indigo-700/40'
                      }`}
                      onClick={() => viewSaved(item.id)}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{item.title}</h4>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                            {new Date(item.createdAt).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                            {' · '}
                            {new Date(item.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          <span className={`text-lg font-bold ${scoreColor}`}>{item.overallHealthScore}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            item.healthLabel === 'Excellent' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' :
                            item.healthLabel === 'Good' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' :
                            item.healthLabel === 'Fair' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400' :
                            'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                          }`}>{item.healthLabel}</span>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">{item.viewerRole}</span>
                          {item.filterStartDate && (
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                              {new Date(item.filterStartDate).toLocaleDateString()} – {item.filterEndDate ? new Date(item.filterEndDate).toLocaleDateString() : 'Now'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); viewSaved(item.id); }}
                            className="p-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition" title="View">
                            <Eye className="w-3.5 h-3.5 text-indigo-500" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(item.id); }}
                            className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 transition" title="Delete">
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        </div>
                      </div>
                      {/* Delete Confirmation */}
                      {deleteConfirm === item.id && (
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600 flex items-center justify-between" onClick={e => e.stopPropagation()}>
                          <p className="text-xs text-red-500 font-medium">Delete this analysis?</p>
                          <div className="flex gap-2">
                            <button onClick={() => setDeleteConfirm(null)}
                              className="px-2.5 py-1 rounded-lg text-[10px] font-medium text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                              Cancel
                            </button>
                            <button onClick={() => handleDelete(item.id)}
                              className="px-2.5 py-1 rounded-lg text-[10px] font-medium bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-500/30 transition">
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default KpiAiDashboardPage;
